import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { cn } from "../../lib/utils";

// Lookup maps
const dimensionOptions = [
  { label: "Date", value: "date" },
  { label: "Domain", value: "domain" },
  { label: "Ad Format", value: "ad_format" },
  { label: "Ad Size", value: "ad_size" },
  { label: "Advertizer", value: "advertizer" },
  { label: "Platform", value: "platform" },
  { label: "Partner", value: "partner" },
  { label: "Category", value: "category" },
  { label: "Article Id", value: "articleId" },
  { label: "Browser", value: "browser" },
  { label: "Server / Client", value: "server_or_client" },
];

const metricOptions = [
  { label: "Revenue", value: "revenue" },
  { label: "Monetized Impressions", value: "monetized_impressions" },
  { label: "Monetized eCPM", value: "monetized_ecpm" },
  { label: "Average Latency", value: "avg_latency" },
  { label: "Request Count", value: "request_count" },
  { label: "Response Count", value: "response_count" },
  { label: "Bid Rate", value: "bid_rate" },
  { label: "Win Rate", value: "win_rate" },
];

const dimensionMap = Object.fromEntries(
  dimensionOptions.map((o) => [o.value, o.label])
);
const metricMap = Object.fromEntries(
  metricOptions.map((o) => [o.value, o.label])
);

const DataTableFirst = ({ data, className }) => {
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let working = [...data];

    // Default date sort (desc)
    if (!sortConfig.key && working[0]?.date) {
      working.sort((a, b) => new Date(b.date) - new Date(a.date));
    }

    // Filter
    if (search.trim() !== "") {
      working = working.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(search.toLowerCase())
        )
      );
    }

    // Sorting
    if (sortConfig.key) {
      working.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];

        if (typeof valA === "number" && typeof valB === "number") {
          return sortConfig.direction === "asc"
            ? valA - valB
            : valB - valA;
        }

        if (sortConfig.key === "date") {
          return sortConfig.direction === "asc"
            ? new Date(valA) - new Date(valB)
            : new Date(valB) - new Date(valA);
        }

        return sortConfig.direction === "asc"
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }

    return working;
  }, [data, search, sortConfig]);

  if (processedData.length === 0) {
    return (
      <p className="text-center py-6 text-muted-foreground">
        No data found
      </p>
    );
  }

  const keys = Object.keys(processedData[0]);

  const getLabel = (key) =>
    dimensionMap[key] ||
    metricMap[key] ||
    key.charAt(0).toUpperCase() + key.slice(1);

  const formatValue = (key, value) => {
    if (metricMap[key]) {
      if (key.includes("revenue") || key.includes("ecpm")) {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(Number(value));
      }
      if (key.includes("impressions") || key.includes("count")) {
        return new Intl.NumberFormat("en-US").format(Number(value));
      }
      if (key.includes("rate") || key.includes("latency")) {
        return Number(value).toFixed(4);
      }
    }
    return value;
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  // CSV export
  const downloadCSV = () => {
    const header = keys.map(getLabel).join(",");
    const rows = processedData.map((row) =>
      keys.map((key) => `"${row[key]}"`).join(",")
    );

    const csvContent = [header, ...rows].join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "export-data.csv");
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={cn("glass-card overflow-hidden animate-fade-in", className)}>
      {/* Search + CSV */}
      <div className="flex justify-between items-center p-3 gap-2">
        <input
          type="text"
          placeholder="Search..."
          className="border rounded px-3 py-2 w-1/3"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button
          onClick={downloadCSV}
          className="px-4 py-2 bg-green-600 text-white rounded"
        >
          CSV
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {keys.map((key) => (
                <TableHead
                  key={key}
                  onClick={() => handleSort(key)}
                  className="cursor-pointer select-none"
                >
                  {getLabel(key)}
                  {sortConfig.key === key && (
                    <span>
                      {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {processedData.map((row, i) => (
              <TableRow key={i}>
                {keys.map((key) => (
                  <TableCell key={key}>
                    {formatValue(key, row[key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default DataTableFirst;
